import { useMemo, useState } from "react";
import { Check, Search, X } from "lucide-react";
import useBrandTranslation from "../hooks/useBrandTranslation";
import { Input } from "./common/Input";
import { cn } from "../utils/utils";
import {
  HEALTH_INSURANCE_GROUPS,
  HEALTH_INSURANCES,
  canonicalInsuranceName,
  insuranceKey,
  sameInsurance,
} from "../utils/healthInsurances";

const OTHER = "__other__";

const selectClass =
  "w-full rounded border border-slate-200 p-2.5 text-slate-700 text-sm bg-white";

/**
 * Cobertura del paciente (o familiar): select con el listado de obras sociales y
 * prepagas + opción "Otra" con texto libre. Controlado: `value` / `onChange(name)`.
 */
export function HealthInsuranceSelect({ id, value, onChange, className }) {
  const { t } = useBrandTranslation();
  const listed = HEALTH_INSURANCES.find((item) => sameInsurance(item, value));
  const [otherMode, setOtherMode] = useState(false);
  const showOther = otherMode || (!!value && !listed);
  const selectValue = showOther ? OTHER : listed || "";

  const handleSelect = (e) => {
    const next = e.target.value;
    if (next === OTHER) {
      setOtherMode(true);
      if (listed) onChange("");
      return;
    }
    setOtherMode(false);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <select id={id} value={selectValue} onChange={handleSelect} className={cn(selectClass, className)}>
        <option value="">{t("app.insurance.none", "Sin cobertura / Seleccionar")}</option>
        {HEALTH_INSURANCE_GROUPS.map((group) => (
          <optgroup key={group.key} label={t(group.labelKey, group.labelDefault)}>
            {group.items.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </optgroup>
        ))}
        <option value={OTHER}>{t("app.insurance.other", "Otra (no está en el listado)")}</option>
      </select>
      {showOther && (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={className}
          placeholder={t("app.insurance.otherPlaceholder", "Escribí el nombre de tu cobertura")}
        />
      )}
    </div>
  );
}

/**
 * Coberturas que atiende un profesional / institución: buscador + lista con
 * checkboxes + chips de las elegidas. Controlado: `value` (array) / `onChange(array)`.
 */
export function HealthInsuranceMultiSelect({ value, onChange }) {
  const { t } = useBrandTranslation();
  const [query, setQuery] = useState("");
  const selected = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const isSelected = (name) => selected.some((s) => sameInsurance(s, name));

  const toggle = (name) => {
    onChange(
      isSelected(name)
        ? selected.filter((s) => !sameInsurance(s, name))
        : [...selected, canonicalInsuranceName(name)]
    );
  };

  const q = insuranceKey(query);
  const matches = (name) =>
    !q || name.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").includes(q);

  return (
    <div className="space-y-3">
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {selected.map((name) => (
            <span
              key={name}
              className="inline-flex items-center gap-1 rounded-full border border-blue-500 bg-blue-50 px-3 py-1 text-sm text-blue-700"
            >
              {name}
              <button
                type="button"
                onClick={() => toggle(name)}
                className="rounded-full p-0.5 hover:bg-blue-100"
                aria-label={t("app.insurance.remove", "Quitar {{name}}", { name })}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500">
          {t("app.insurance.noneSelected", "Todavía no elegiste coberturas: los pacientes solo verán tus turnos particulares.")}
        </p>
      )}

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9"
          placeholder={t("app.insurance.searchPlaceholder", "Buscar obra social o prepaga...")}
        />
      </div>

      <div className="max-h-72 overflow-y-auto rounded border border-slate-200 bg-white">
        {HEALTH_INSURANCE_GROUPS.map((group) => {
          const items = group.items.filter(matches);
          if (items.length === 0) return null;
          return (
            <div key={group.key}>
              <p className="sticky top-0 z-10 bg-slate-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t(group.labelKey, group.labelDefault)}
              </p>
              {items.map((name) => {
                const checked = isSelected(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => toggle(name)}
                    className={cn(
                      "flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50",
                      checked && "text-blue-700"
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border",
                        checked ? "border-blue-500 bg-blue-500 text-white" : "border-slate-300"
                      )}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    {name}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
